// src/services/NotificationService.js - MULTI-DEVICE SUPPORT

import { db, auth } from '../firebase/config';
import { 
  collection, 
  addDoc, 
  query, 
  where, 
  orderBy, 
  onSnapshot,
  updateDoc,
  doc,
  serverTimestamp,
  getDocs,
  setDoc,
  getDoc,
  deleteDoc,
  writeBatch
} from 'firebase/firestore';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';

class NotificationService {
  static messaging = null;
  static currentToken = null;
  static currentDeviceId = null;
  static initializationPromise = null;
  
  // ✅ NEW: Generate stable device ID
  static getDeviceId() {
    // Check if we already have a device ID
    let deviceId = localStorage.getItem('device_id');
    
    if (!deviceId) {
      // Generate new device ID
      deviceId = 'device_' + Date.now() + '_' + Math.random().toString(36).substring(2, 15);
      localStorage.setItem('device_id', deviceId);
    }
    
    return deviceId;
  }
  
  // ✅ NEW: Get device info
  static getDeviceInfo() {
    const ua = navigator.userAgent;
    let deviceType = 'desktop';
    let platform = 'unknown';
    let browser = 'unknown';
    
    // Detect platform
    if (/android/i.test(ua)) platform = 'android';
    else if (/iPad|iPhone|iPod/.test(ua)) platform = 'ios';
    else if (/Win/.test(ua)) platform = 'windows';
    else if (/Mac/.test(ua)) platform = 'mac';
    else if (/Linux/.test(ua)) platform = 'linux';
    
    // Detect device type
    if (/Mobile|Android|iPhone/i.test(ua)) deviceType = 'mobile';
    else if (/iPad|Tablet/i.test(ua)) deviceType = 'tablet';
    
    // Detect browser
    if (ua.includes('Chrome')) browser = 'Chrome';
    else if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Safari')) browser = 'Safari';
    else if (ua.includes('Edge')) browser = 'Edge';
    
    // Check if PWA
    const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
                  window.navigator.standalone === true;
    
    return {
      deviceId: this.getDeviceId(),
      platform,
      browser,
      deviceType,
      isPWA,
      userAgent: ua
    };
  }
  
  // ✅ UPDATED: Save token with device ID
  static async saveFCMToken(userId, token) {
    if (!token || !userId) return;
    
    try {
      const deviceInfo = this.getDeviceInfo();
      const deviceId = deviceInfo.deviceId;
      
      console.log('💾 Saving FCM token for device:', deviceId);
      
      // Save to fcmTokens/{userId}/devices/{deviceId}
      const deviceRef = doc(db, 'fcmTokens', userId, 'devices', deviceId);
      
      await setDoc(deviceRef, {
        token,
        userId,
        ...deviceInfo,
        createdAt: serverTimestamp(),
        lastUsed: serverTimestamp()
      }, { merge: true });
      
      // Also cache locally
      localStorage.setItem(`fcm_token_${userId}_${deviceId}`, token);
      localStorage.setItem(`fcm_token_timestamp_${userId}_${deviceId}`, Date.now().toString());
      
      this.currentDeviceId = deviceId;
      this.currentToken = token;
      
      console.log('✅ FCM token saved for device:', deviceId);
      
    } catch (error) {
      console.error('❌ Error saving FCM token:', error);
    }
  }
  
  // ✅ UPDATED: Get all tokens for user
  static async getAllUserTokens(userId) {
    try {
      const devicesRef = collection(db, 'fcmTokens', userId, 'devices');
      const devicesSnap = await getDocs(devicesRef);
      
      const tokens = [];
      devicesSnap.forEach(doc => {
        const data = doc.data();
        tokens.push({
          deviceId: doc.id,
          token: data.token,
          platform: data.platform,
          browser: data.browser,
          deviceType: data.deviceType,
          isPWA: data.isPWA,
          lastUsed: data.lastUsed
        });
      });
      
      console.log(`📱 Found ${tokens.length} devices for user ${userId}`);
      return tokens;
      
    } catch (error) {
      console.error('Error getting user tokens:', error);
      return [];
    }
  }
  
  // ✅ UPDATED: Check cached token for THIS device
  static async ensureFCMToken(userId) {
    try {
      console.log('🔄 Ensuring FCM token for user:', userId);
      
      const deviceId = this.getDeviceId();
      console.log('📱 Device ID:', deviceId);
      
      // Check localStorage cache for THIS device
      const cachedToken = localStorage.getItem(`fcm_token_${userId}_${deviceId}`);
      const cacheTimestamp = localStorage.getItem(`fcm_token_timestamp_${userId}_${deviceId}`);
      
      if (cachedToken && cacheTimestamp) {
        const age = Date.now() - parseInt(cacheTimestamp);
        // Use cached token if less than 7 days old
        if (age < 604800000 && !cachedToken.startsWith('dev-mode-') && !cachedToken.startsWith('fallback-')) {
          console.log('✅ Using cached FCM token for this device');
          this.currentToken = cachedToken;
          this.currentDeviceId = deviceId;
          return cachedToken;
        }
      }
      
      // Try to get existing token from Firestore for THIS device
      const deviceRef = doc(db, 'fcmTokens', userId, 'devices', deviceId);
      const deviceSnap = await getDoc(deviceRef);
      
      if (deviceSnap.exists()) {
        const existingToken = deviceSnap.data().token;
        if (existingToken && 
            existingToken.length > 10 && 
            !existingToken.startsWith('mock-') &&
            !existingToken.startsWith('dev-mode-') &&
            !existingToken.startsWith('fallback-')) {
          console.log('✅ Using existing valid FCM token from Firestore for this device');
          
          // Update lastUsed
          await updateDoc(deviceRef, {
            lastUsed: serverTimestamp()
          });
          
          // Cache it
          localStorage.setItem(`fcm_token_${userId}_${deviceId}`, existingToken);
          localStorage.setItem(`fcm_token_timestamp_${userId}_${deviceId}`, Date.now().toString());
          
          this.currentToken = existingToken;
          this.currentDeviceId = deviceId;
          
          return existingToken;
        }
      }
      
      // Try to generate new FCM token with timeout
      console.log('🔄 Attempting to generate new FCM token...');
      
      const timeout = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Token generation timeout')), 10000)
      );
      
      try {
        const newToken = await Promise.race([
          this.initializeFCM(),
          timeout
        ]);
        
        if (newToken && newToken.length > 10 && !newToken.startsWith('mock-')) {
          await this.saveFCMToken(userId, newToken);
          
          console.log('✅ New FCM token generated and saved for this device');
          return newToken;
        }
      } catch (timeoutError) {
        console.warn('⏱️ Token generation timed out');
      }
      
      // Return null instead of fake token
      console.warn('⚠️ FCM token generation failed - push notifications unavailable');
      
      localStorage.setItem(`fcm_failed_${userId}_${deviceId}`, 'true');
      localStorage.setItem(`fcm_failed_reason_${userId}_${deviceId}`, 'timeout');
      
      return null;
      
    } catch (error) {
      console.error('❌ Error ensuring FCM token:', error);
      
      const deviceId = this.getDeviceId();
      localStorage.setItem(`fcm_failed_${userId}_${deviceId}`, 'true');
      localStorage.setItem(`fcm_failed_reason_${userId}_${deviceId}`, error.message);
      
      return null;
    }
  }
  
  // ✅ UPDATED: Cleanup old/invalid tokens
  static async cleanupInvalidTokens(userId) {
    try {
      console.log('🧹 Cleaning up invalid tokens for user:', userId);
      
      const devicesRef = collection(db, 'fcmTokens', userId, 'devices');
      const devicesSnap = await getDocs(devicesRef);
      
      const batch = writeBatch(db);
      let deletedCount = 0;
      
      const now = Date.now();
      const maxAge = 30 * 24 * 60 * 60 * 1000; // 30 days
      
      devicesSnap.forEach(doc => {
        const data = doc.data();
        const lastUsed = data.lastUsed?.toMillis() || data.createdAt?.toMillis() || 0;
        const age = now - lastUsed;
        
        // Delete if:
        // 1. Token is invalid/mock
        // 2. Last used > 30 days ago
        if (
          data.token?.startsWith('mock-') ||
          data.token?.startsWith('dev-mode-') ||
          data.token?.startsWith('fallback-') ||
          age > maxAge
        ) {
          console.log(`🗑️ Deleting stale device: ${doc.id}`);
          batch.delete(doc.ref);
          deletedCount++;
        }
      });
      
      if (deletedCount > 0) {
        await batch.commit();
        console.log(`✅ Cleaned up ${deletedCount} invalid tokens`);
      } else {
        console.log('✅ No invalid tokens to clean');
      }
      
    } catch (error) {
      console.error('Error cleaning up tokens:', error);
    }
  }
  
  // ✅ UPDATED: Delete THIS device's token
  static async deleteDeviceToken(userId, deviceId = null) {
    try {
      const targetDeviceId = deviceId || this.getDeviceId();
      
      console.log('🗑️ Deleting token for device:', targetDeviceId);
      
      const deviceRef = doc(db, 'fcmTokens', userId, 'devices', targetDeviceId);
      await deleteDoc(deviceRef);
      
      // Clear cache
      localStorage.removeItem(`fcm_token_${userId}_${targetDeviceId}`);
      localStorage.removeItem(`fcm_token_timestamp_${userId}_${targetDeviceId}`);
      
      console.log('✅ Device token deleted');
      
    } catch (error) {
      console.error('Error deleting device token:', error);
    }
  }
  
  // ✅ UPDATED: Delete ALL tokens for user (on full logout)
  static async deleteAllUserTokens(userId) {
    try {
      console.log('🗑️ Deleting ALL tokens for user:', userId);
      
      const devicesRef = collection(db, 'fcmTokens', userId, 'devices');
      const devicesSnap = await getDocs(devicesRef);
      
      const batch = writeBatch(db);
      
      devicesSnap.forEach(doc => {
        batch.delete(doc.ref);
      });
      
      await batch.commit();
      
      // Clear all local caches
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.includes(`fcm_token_${userId}`) || key.includes(`fcm_failed_${userId}`)) {
          localStorage.removeItem(key);
        }
      });
      
      console.log(`✅ Deleted ${devicesSnap.size} device tokens`);
      
    } catch (error) {
      console.error('Error deleting all user tokens:', error);
    }
  }
  
  // ✅ UPDATED: Send push to ALL user's devices
  static async sendPushNotificationViaBackend(userId, notificationData) {
  try {
    console.log('📤 Sending push notification via backend for user:', userId);
    
    // ✅ REMOVED: Don't query tokens from frontend
    // The backend will query them with Firebase Admin SDK privileges
    
    const apiUrl = '/api/send-notification';
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        userId,
        notification: notificationData,
        sendToAllDevices: true // Backend will get all devices
      })
    });

    console.log('📥 Backend response status:', response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Backend error:', errorText);
      throw new Error('Backend notification failed');
    }
    
    const result = await response.json();
    
    if (!result.success && result.error) {
      console.error('❌ Backend returned error:', result.error);
    } else {
      console.log('✅ Backend notification result:', {
        push: result.push?.success ? '✅ Sent' : '❌ Failed',
        email: result.email?.success ? '✅ Sent' : '❌ Failed',
        sentCount: result.push?.sentCount || 0,
        totalDevices: result.push?.totalDevices || 0
      });
    }
    
    return result;
    
  } catch (error) {
    console.error('❌ Error sending push notification:', error);
    
    return { 
      success: false, 
      error: error.message,
      fallback: 'in-app-only'
    };
  }
}
  
  // Rest of the NotificationService methods remain the same...
  // (initializeFCM, setupForegroundMessageHandler, createNotification, etc.)
  // Just make sure they use the updated token saving methods
  
  static async detectBrave() {
    try {
      return (navigator.brave && await navigator.brave.isBrave()) || false;
    } catch {
      return false;
    }
  }

  static async detectIOSPWA() {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isStandalone = window.navigator.standalone === true ||
                        window.matchMedia('(display-mode: standalone)').matches;
    
    return isIOS && isStandalone;
  }

  static async detectIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  }
  
  static shouldSkipFCM() {
    const isLocalhost = window.location.hostname === 'localhost' || 
                        window.location.hostname === '127.0.0.1';
    const isHTTP = window.location.protocol !== 'https:';
    
    if (isLocalhost || isHTTP) {
      console.log('ℹ️ Skipping FCM - not production environment');
      return true;
    }
    
    if (!('serviceWorker' in navigator) || 
        !('Notification' in window) || 
        !('PushManager' in window)) {
      console.log('ℹ️ Skipping FCM - browser not supported');
      return true;
    }
    
    return false;
  }

  static async initializeFCM() {
    console.log('🔍 Starting FCM initialization');
    
    if (await this.detectIOSPWA()) {
      console.log('📱 iOS PWA detected - using in-app notifications only');
      localStorage.setItem('notification_mode', 'in-app-only');
      return null;
    }
    
    if (await this.detectIOS() && !await this.detectIOSPWA()) {
      console.log('📱 iOS Safari - FCM support limited');
    }
    
    if (this.initializationPromise) {
      console.log('⏳ FCM initialization already in progress');
      return this.initializationPromise;
    }
    
    if (this.shouldSkipFCM()) {
      return null;
    }
    
    const isBraveBrowser = await this.detectBrave();
    const isSlowConnection = navigator.connection?.effectiveType === 'slow-2g' || 
                             navigator.connection?.effectiveType === '2g';
    
    let tokenTimeout = 8000;
    if (isBraveBrowser) tokenTimeout = 15000;
    if (isSlowConnection) tokenTimeout = 20000;
    
    console.log(`⏱️ Using ${tokenTimeout}ms timeout for FCM token`);
    
    this.initializationPromise = (async () => {
      try {
        const permission = Notification.permission;
        
        if (permission === 'denied') {
          console.log('❌ Notifications denied by user');
          localStorage.setItem('notification_mode', 'in-app-only');
          return null;
        }
        
        if (permission !== 'granted') {
          const newPermission = await Promise.race([
            Notification.requestPermission(),
            new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Permission timeout')), 8000)
            )
          ]);
          
          if (newPermission !== 'granted') {
            localStorage.setItem('notification_mode', 'in-app-only');
            return null;
          }
        }

        let registration;
        let swAttempts = 0;
        
        while (swAttempts < 3) {
          try {
            registration = await Promise.race([
              navigator.serviceWorker.register('/service-worker.js', { scope: '/' }),
              new Promise((_, reject) => 
                setTimeout(() => reject(new Error('SW registration timeout')), 8000)
              )
            ]);
            break;
          } catch (swError) {
            swAttempts++;
            if (swAttempts >= 3) throw swError;
            console.warn(`⚠️ SW registration attempt ${swAttempts} failed, retrying...`);
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        }
        
        await Promise.race([
          navigator.serviceWorker.ready,
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('SW ready timeout')), 5000)
          )
        ]);
        
        console.log('✅ Service Worker ready');

        this.messaging = getMessaging();

        const vapidKey = 'BEEt5GXG1gmPm9eleFsE0mN1HF8_qc7atMwnxt66nNEwttfyla71TdQeJg5xPoXJdWaGt8etld6MXokP0RhgoJk';
        
        let token;
        let tokenAttempts = 0;
        
        while (tokenAttempts < 3) {
          try {
            token = await Promise.race([
              getToken(this.messaging, {
                vapidKey,
                serviceWorkerRegistration: registration
              }),
              new Promise((_, reject) => 
                setTimeout(() => reject(new Error('FCM token timeout')), tokenTimeout)
              )
            ]);
            
            if (token) break;
            
          } catch (tokenError) {
            tokenAttempts++;
            if (tokenAttempts >= 3) {
              console.error('❌ Failed to get FCM token after 3 attempts');
              localStorage.setItem('notification_mode', 'in-app-only');
              localStorage.setItem('fcm_token_failed', 'true');
              return null;
            }
            
            console.warn(`⚠️ FCM token attempt ${tokenAttempts} failed, retrying...`);
            await new Promise(resolve => setTimeout(resolve, 2000 * tokenAttempts));
          }
        }
        
        if (token) {
          console.log('✅ FCM Token obtained successfully');
          this.currentToken = token;
          localStorage.setItem('notification_mode', 'push-enabled');
          localStorage.removeItem('fcm_token_failed');
          this.setupForegroundMessageHandler();
          return token;
        } else {
          console.warn('⚠️ No FCM token - falling back to in-app only');
          localStorage.setItem('notification_mode', 'in-app-only');
          return null;
        }
        
      } catch (error) {
        console.error('❌ FCM initialization error:', error);
        localStorage.setItem('notification_mode', 'in-app-only');
        localStorage.setItem('fcm_token_failed', 'true');
        return null;
        
      } finally {
        this.initializationPromise = null;
      }
    })();
    
    return this.initializationPromise;
  }

  static setupForegroundMessageHandler() {
    if (!this.messaging) {
      console.warn('❌ Messaging not initialized, skipping foreground handler');
      return;
    }

    console.log('🔔 Setting up foreground message handler');
    
    onMessage(this.messaging, async (payload) => {
      console.log('📨 Foreground message received:', payload);
      
      const appState = await this.getAccurateAppState();
      
      console.log('📱 Current app state:', appState);
      
      if (appState === 'visible') {
        console.log('👁️ App visible - showing BOTH toast + system notification');
        
        this.showToast(
          payload.notification?.title, 
          payload.notification?.body,
          payload.data?.actionUrl
        );
        
        if (Notification.permission === 'granted') {
          const notification = new Notification(
            payload.notification?.title || 'KalKode',
            {
              body: payload.notification?.body || 'You have a new notification',
              tag: payload.data?.tag || 'notification-' + Date.now(),
              data: { 
                url: payload.data?.actionUrl || '/messages',
                ...payload.data 
              },
              silent: true,
              requireInteraction: false,
              renotify: false
            }
          );
          
          notification.onclick = () => {
            window.focus();
            if (payload.data?.actionUrl) {
              window.location.href = payload.data.actionUrl;
            }
            notification.close();
          };
        }
        
        this.playNotificationSound();
        this.vibrate();
        
      } else {
        console.log('🔕 App hidden/background - showing system notification only');
        
        if (Notification.permission === 'granted') {
          const notification = new Notification(
            payload.notification?.title || 'KalKode',
            {
              body: payload.notification?.body || 'You have a new notification',
              tag: payload.data?.tag || 'notification-' + Date.now(),
              data: { 
                url: payload.data?.actionUrl || '/messages',
                ...payload.data 
              },
              vibrate: [200, 100, 200],
              requireInteraction: payload.data?.priority === 'high',
              silent: false,
              renotify: true,
              actions: [
                { action: 'open', title: 'Open' },
                { action: 'close', title: 'Dismiss' }
              ]
            }
          );
          
          notification.onclick = () => {
            window.focus();
            if (payload.data?.actionUrl) {
              window.location.href = payload.data.actionUrl;
            }
            notification.close();
          };
        }
        
        this.playNotificationSound();
        this.vibrate();
      }
      
      if (payload.data?.userId) {
        this.updateBadgeCount(payload.data.userId);
      }
    });
  }

  static async getAccurateAppState() {
    if (document.hidden) {
      return 'hidden';
    }
    
    if (!document.hasFocus()) {
      return 'background';
    }
    
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      try {
        const clients = await navigator.serviceWorker.ready.then(reg => 
          reg.active?.clients?.matchAll({ type: 'window', includeUncontrolled: true })
        );
        
        if (clients && clients.length > 0) {
          const hasFocusedClient = clients.some(client => 
            client.visibilityState === 'visible' && client.focused
          );
          
          if (!hasFocusedClient) {
            return 'background';
          }
        }
      } catch (error) {
        console.warn('Could not check service worker clients:', error);
      }
    }
    
    return 'visible';
  }

  static showToast(title, message, actionUrl) {
    const existingToasts = document.querySelectorAll('.kalkode-notification-toast');
    existingToasts.forEach(toast => toast.remove());
    
    let headerHeight = 80;
    
    const navMenu = document.querySelector('[class*="NavMenuContainer"]');
    if (navMenu && window.innerWidth > 768) {
      headerHeight = navMenu.offsetHeight + 10;
    }
    
    const mobileHeader = document.querySelector('header');
    if (mobileHeader && window.innerWidth <= 768) {
      headerHeight = mobileHeader.offsetHeight + 10;
    }
    
    if (window.location.pathname === '/notifications') {
      const notifHeader = document.querySelector('[class*="NotificationsHeader"]');
      if (notifHeader) {
        headerHeight = notifHeader.offsetHeight + 10;
      }
    }
    
    const toast = document.createElement('div');
    toast.className = 'kalkode-notification-toast';
    
    toast.style.cssText = `
      position: fixed;
      top: ${headerHeight}px;
      left: 50%;
      transform: translateX(-50%) translateY(-150%);
      background: linear-gradient(135deg, rgba(0, 0, 0, 0.98), rgba(26, 26, 76, 0.98));
      border: 2px solid rgba(128, 0, 0, 0.6);
      border-radius: 16px;
      padding: 1rem 1.25rem;
      max-width: calc(100vw - 40px);
      width: 420px;
      box-shadow: 0 12px 32px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(128, 0, 0, 0.3);
      z-index: 10001;
      cursor: pointer;
      transition: transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.3s ease;
      backdrop-filter: blur(10px);
      opacity: 0;
    `;
    
    toast.innerHTML = `
      <div style="display: flex; align-items: flex-start; gap: 1rem; position: relative;">
        <img 
          src="https://kalkode.com/logo512.png" 
          alt="KalKode"
          style="width: 48px; height: 48px; border-radius: 12px; flex-shrink: 0; box-shadow: 0 4px 8px rgba(0,0,0,0.3);"
        />
        <div style="flex: 1; min-width: 0; padding-right: 2rem;">
          <div style="color: #FFFFFF; font-weight: 700; margin-bottom: 0.5rem; font-size: 1rem; line-height: 1.3;">
            ${title}
          </div>
          <div style="color: rgba(255, 255, 255, 0.9); font-size: 0.9rem; line-height: 1.4; word-wrap: break-word;">
            ${message}
          </div>
          <div style="color: #800000; font-size: 0.85rem; font-weight: 600; margin-top: 0.75rem; display: flex; align-items: center; gap: 0.25rem;">
            <span>Tap to view</span>
            <span style="font-size: 1.2rem;">→</span>
          </div>
        </div>
        <button 
          class="toast-close-btn"
          style="
            position: absolute;
            top: 0;
            right: 0;
            background: rgba(255, 255, 255, 0.1);
            border: none;
            border-radius: 50%;
            width: 28px;
            height: 28px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            color: rgba(255, 255, 255, 0.7);
            transition: all 0.2s;
            font-size: 1.2rem;
            line-height: 1;
            padding: 0;
            font-weight: 300;
          "
        >×</button>
      </div>
    `;
    
    const closeBtn = toast.querySelector('.toast-close-btn');
    
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      removeToast();
    });
    
    closeBtn.addEventListener('mouseenter', (e) => {
      e.target.style.background = 'rgba(244, 67, 54, 0.3)';
      e.target.style.color = '#F44336';
    });
    
    closeBtn.addEventListener('mouseleave', (e) => {
      e.target.style.background = 'rgba(255, 255, 255, 0.1)';
      e.target.style.color = 'rgba(255, 255, 255, 0.7)';
    });
    
    toast.addEventListener('click', () => {
      if (actionUrl) {
        window.location.href = actionUrl;
      }
      removeToast();
    });
    
    document.body.appendChild(toast);
    
    requestAnimationFrame(() => {
      setTimeout(() => {
        toast.style.transform = 'translateX(-50%) translateY(0)';
        toast.style.opacity = '1';
      }, 10);
    });
    
    const removeToast = () => {
      toast.style.transform = 'translateX(-50%) translateY(-150%)';
      toast.style.opacity = '0';
      setTimeout(() => {
        if (document.body.contains(toast)) {
          document.body.removeChild(toast);
        }
      }, 500);
    };
    
    const autoRemoveTimer = setTimeout(removeToast, 6000);
    
    closeBtn.addEventListener('click', () => {
      clearTimeout(autoRemoveTimer);
    });
    
    toast.addEventListener('click', () => {
      clearTimeout(autoRemoveTimer);
    });
  }

  static playNotificationSound() {
    try {
      const audio = new Audio('/notification-sound.mp3');
      audio.volume = 0.5;
      audio.play().catch(err => console.log('Could not play sound:', err));
    } catch (error) {
      console.log('Notification sound not available');
    }
  }

  static vibrate() {
    if ('vibrate' in navigator) {
      navigator.vibrate([200, 100, 200]);
    }
  }

  static async createNotification(userId, notificationData) {
    try {
      // Step 1: Create in-app notification
    const notification = {
      userId,
      ...notificationData,
      read: false,
      timestamp: serverTimestamp(),
      createdAt: new Date().toISOString()
    };

    const docRef = await addDoc(collection(db, 'notifications'), notification);
    console.log('✅ In-app notification created:', docRef.id);
    
    // Step 2: Prepare data for backend (push + email)
    const enhancedNotificationData = {
      ...notificationData,
      userId: userId,
      icon: 'https://kalkode.com/logo512.png',
      tag: notificationData.transactionId || notificationData.chatId || 'notification-' + Date.now(),
      sendEmail: notificationData.sendEmail !== false // Default to true
    };
    
    // Step 3: Send push notification via backend
    try {
      const backendResult = await this.sendPushNotificationViaBackend(userId, enhancedNotificationData);
      
      // Log results
      if (backendResult.push?.success) {
        console.log(`✅ Push sent to ${backendResult.push.sentCount}/${backendResult.push.totalDevices} devices`);
      } else {
        console.log('ℹ️ Push notification unavailable:', backendResult.push?.message || 'Unknown reason');
      }
      
      if (backendResult.email?.success) {
          console.log('✅ Email notification sent');
        } else if (backendResult.email?.reason) {
          console.log(`ℹ️ Email not sent: ${backendResult.email.reason}`);
        }

      } catch (backendError) {
        console.error('❌ Backend notification failed:', backendError);
        // Don't throw - in-app notification was still created
      }

      // Step 4: DON'T update badge here - that should happen on recipient's device
      // The badge will be updated when the recipient loads the notifications page

      return docRef.id;
    } catch (error) {
      console.error('Error creating notification:', error);
      throw error;
    }
  }

  static setupNotificationsListener(userId, callback) {
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      orderBy('timestamp', 'desc')
    );

    return onSnapshot(q, (snapshot) => {
      const notifications = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      callback(notifications);
      
      const unreadCount = notifications.filter(n => !n.read).length;
      this.setBadge(unreadCount);
    });
  }

  static async markNotificationAsRead(notificationId) {
    try {
      await updateDoc(doc(db, 'notifications', notificationId), {
        read: true,
        readAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }

  static async markAllAsRead(userId) {
    try {
      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        where('read', '==', false)
      );
      
      const snapshot = await getDocs(q);
      const updates = snapshot.docs.map(doc => 
        updateDoc(doc.ref, { read: true, readAt: serverTimestamp() })
      );
      
      await Promise.all(updates);
      await this.setBadge(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  }

  static async updateBadgeCount(userId) {
    if (!userId) return;
    
    // ✅ Only update badge if we're the user being notified
  const currentUser = auth.currentUser;
  if (!currentUser || currentUser.uid !== userId) {
    console.log('ℹ️ Skipping badge update - not current user');
    return;
  }
  
  try {
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      where('read', '==', false)
      );

      const snapshot = await getDocs(q);
      const count = snapshot.size;

      await this.setBadge(count);
      console.log(`✅ Badge updated to ${count}`);

    } catch (error) {
      console.error('Error updating badge:', error);
    }
  }

  static async setBadge(count) {
    if (!('setAppBadge' in navigator)) {
      console.log('ℹ️ Badge API not supported on this browser');
      return;
    }
    
    try {
      if (count > 0) {
        await navigator.setAppBadge(count);
      } else {
        await navigator.clearAppBadge();
      }
    } catch (error) {
      console.warn('Badge API failed:', error);
    }
  }

  static async clearBadge() {
    if ('clearAppBadge' in navigator) {
      try {
        await navigator.clearAppBadge();
      } catch (error) {
        console.error('Error clearing badge:', error);
      }
    }
  }

  // cartItems: [{ itemName, quantity, unitPrice, customization }]
  static async notifyNewOrderRequest(sellerId, buyerName, cartItems, total, transactionId, autoAccepted = false, shopName = '') {
    const totalUnits = cartItems.reduce((sum, i) => sum + (i.quantity || 1), 0);
    const amountStr = total ? ` · $${parseFloat(total).toFixed(2)}` : '';
    const pushTitle = autoAccepted
      ? `Order from ${buyerName}`
      : cartItems.length === 1
        ? `New order from ${buyerName}`
        : `New order from ${buyerName} · ${cartItems.length} items`;
    const pushMessage = cartItems.length === 1
      ? `${cartItems[0].quantity}× ${cartItems[0].itemName}${amountStr}`
      : `${totalUnits} items total${amountStr}`;

    const payload = {
      type: 'transaction',
      subType: 'new_order',
      title: pushTitle,
      message: autoAccepted ? `Auto-confirmed · ${pushMessage}` : pushMessage,
      transactionId,
      actionUrl: `/messages?chat=${transactionId}`,
      priority: 'high',
      buyerName,
      cartItems,
      total: total || 0,
      autoAccepted,
      shopName,
      sendEmail: true
    };

    // Notify the shop owner
    await this.createNotification(sellerId, payload);

    // Fan-out: also notify all shared users with access to this shop
    try {
      const shopRef = doc(db, 'shops', sellerId);
      const shopSnap = await getDoc(shopRef);
      if (shopSnap.exists()) {
        const sharedUserMap = shopSnap.data().sharedUserMap || {};
        const sharedUids = Object.keys(sharedUserMap).filter(uid => uid !== sellerId);
        await Promise.all(
          sharedUids.map(uid =>
            this.createNotification(uid, { ...payload })
          )
        );
      }
    } catch (err) {
      console.warn('⚠️ Failed to fan-out order notification to shared users (non-critical):', err);
    }
  }

  static async notifyOrderAccepted(buyerId, sellerName, itemName, quantity, total, transactionId) {
    return this.createNotification(buyerId, {
      type: 'transaction',
      subType: 'order_accepted',
      title: `${sellerName} accepted your order`,
      message: `${quantity}× ${itemName} · $${parseFloat(total).toFixed(2)} — proceed to payment`,
      transactionId,
      actionUrl: `/messages?chat=${transactionId}`,
      priority: 'high',
      sellerName,
      itemName,
      quantity,
      total
    });
  }

  static async notifyPaymentReceived(sellerId, buyerName, itemName, amount, transactionId) {
    return this.createNotification(sellerId, {
      type: 'transaction',
      subType: 'payment_received',
      title: `Payment received · $${parseFloat(amount).toFixed(2)}`,
      message: `${buyerName} paid for ${itemName} — share your pickup location`,
      transactionId,
      actionUrl: `/messages?chat=${transactionId}`,
      priority: 'high',
      buyerName,
      itemName,
      amount
    });
  }

  static async notifyPickupLocationShared(buyerId, sellerName, location, transactionId) {
    return this.createNotification(buyerId, {
      type: 'transaction',
      subType: 'location_shared',
      title: `Pickup location ready`,
      message: `${sellerName} shared a meetup spot: ${location}`,
      transactionId,
      actionUrl: `/messages?chat=${transactionId}`,
      priority: 'high',
      sellerName,
      location
    });
  }

  static async notifyTransactionCompleted(userId, isSellerView, itemName, amount, transactionId) {
    return this.createNotification(userId, {
      type: 'transaction',
      subType: 'completed',
      title: isSellerView ? `Sale complete · $${parseFloat(amount).toFixed(2)}` : `Order complete`,
      message: isSellerView
        ? `You sold ${itemName}. Funds will settle shortly.`
        : `Your ${itemName} order is done. Leave a review!`,
      transactionId,
      actionUrl: `/messages?chat=${transactionId}`,
      priority: 'normal',
      isSellerView,
      itemName,
      amount
    });
  }

  static async notifyOrderRejected(buyerId, sellerName, itemName, transactionId) {
    return this.createNotification(buyerId, {
      type: 'transaction',
      subType: 'order_rejected',
      title: `Order declined`,
      message: `${sellerName} couldn't fulfill your order for ${itemName}`,
      transactionId,
      actionUrl: `/messages?chat=${transactionId}`,
      priority: 'normal',
      sellerName,
      itemName
    });
  }

  static async notifyPaymentWithdrawn(sellerId, buyerName, itemName, transactionId) {
    return this.createNotification(sellerId, {
      type: 'transaction',
      subType: 'payment_withdrawn',
      title: `Payment withdrawn`,
      message: `${buyerName} withdrew their payment for ${itemName}`,
      transactionId,
      actionUrl: `/messages?chat=${transactionId}`,
      priority: 'normal',
      buyerName,
      itemName
    });
  }

  static async notifyNewMessage(recipientId, senderName, message, chatId) {
    return this.createNotification(recipientId, {
      type: 'message',
      subType: 'new_message',
      title: senderName,
      message: message.length > 80 ? message.slice(0, 80) + '…' : message,
      chatId,
      actionUrl: `/messages?chat=${chatId}`,
      priority: 'normal',
      senderName
    });
  }

  static async disablePushNotifications(userId) {
    try {
      await this.deleteDeviceToken(userId);
      
      this.currentToken = null;
      
      console.log('🔕 Push notifications disabled for this device');
      
      return true;
    } catch (error) {
      console.error('❌ Error disabling push notifications:', error);
      throw error;
    }
  }

  static async isPushEnabled(userId) {
    try {
      const deviceId = this.getDeviceId();
      const deviceRef = doc(db, 'fcmTokens', userId, 'devices', deviceId);
      const deviceSnap = await getDoc(deviceRef);
      
      return deviceSnap.exists() && Notification.permission === 'granted';
    } catch (error) {
      console.error('Error checking push status:', error);
      return false;
    }
  }

  static async requestPushPermission() {
    try {
      const token = await this.initializeFCM();
      
      if (token) {
        this.setupForegroundMessageHandler();
        return token;
      }
      
      return null;
    } catch (error) {
      console.error('Error requesting push permission:', error);
      return null;
    }
  }
}

export default NotificationService;